import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useProTools } from '../pro';
import { ADDONS_HIDDEN } from '../../lib/hiddenFeatures';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import { Menu, useMenu, type MenuEntry } from '../../ui/menu';
import type { ToolId } from '../useEditorCanvas';
import { KINDS } from '../tools/shapes';
import { ALL_TOOLS } from './Toolbar';
import type { CreateEditDockProps } from './CreateEditDock';
import { SelectionActionsBar } from './SelectionActionsBar';
import { SelectionModePicker } from './SelectionModePicker';

/**
 * The bottom of the editor at phone width: a tray along the bottom edge with every tool
 * laid out by name in a grid, nothing hidden behind More (Mohamad's pick 5 of five,
 * 2026-10-06, https://claude.ai/artifact/8e2JCAPmBrHwFbdMsUQP8A). The floating dock's
 * buttons were too small to hit with a finger, and its Create / Edit switch was one more
 * thing to read.
 *
 * Nothing chosen, it holds the tools; something chosen, that thing's commands (the shelf's
 * own selection actions bar, so the two never disagree; its look and values are in
 * Properties) and a row of what is done to a layer. Its handle pulls it down to one row while
 * drawing, and back up.
 */
export function PhoneToolTray(props: CreateEditDockProps & { onProperties: () => void; strip?: ReactNode }) {
  const { tool, nodes, onTool, hiddenTools, actions } = props;
  const [folded, setFolded] = useState(false);
  const pro = useProTools();
  /**
   * How tall the tray stands, with the strip over it: the zoom pill and the AI runs bar ride on its
   * top edge (Artwork A), so the root carries the height for them, as it carries the Animate bar's.
   */
  const column = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = column.current;
    if (!element) return;
    const root = document.documentElement.style;
    const measure = () => root.setProperty('--phone-tray-height', `${Math.ceil(element.getBoundingClientRect().height)}px`);
    measure();
    const watcher = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    watcher?.observe(element);
    return () => {
      watcher?.disconnect();
      root.removeProperty('--phone-tray-height');
    };
  }, []);
  const editing = nodes.length > 0 && tool === 'select' && !props.editingPoints;
  const selectionKey = nodes.map((node) => node.id).join(',');

  const shown = (id: ToolId) => !hiddenTools.has(id);
  const mark = (id: ToolId) => ALL_TOOLS.find((one) => one.id === id);
  const shapeKind = KINDS.find((kind) => kind.id === tool) ?? KINDS[0]!;
  const shapes: MenuEntry[] = KINDS.map((kind) => ({ id: kind.id, label: kind.title, icon: kind.icon, checked: tool === kind.id, onSelect: () => onTool(kind.id) }));
  const textItems: MenuEntry[] = props.textModes.map((mode) => ({ id: mode.id, label: mode.title.split(' — ')[0]!, icon: mode.icon, checked: mode.pressed, onSelect: () => { mode.run({ more: false }); onTool('text'); } }));
  const selectionPicker = <SelectionModePicker key="select" modes={props.selectionModes} active={tool === 'select'} phone />;
  /** The tools that are not in the grid, by name. */
  const rest: MenuEntry[] = [
    ...(['warp', 'liquify', 'builder', 'cut', 'blend', 'measure', 'slice'] as const).flatMap((id): MenuEntry[] => {
      const one = mark(id);
      return one && shown(id) ? [{ id, label: one.label, icon: one.icon, checked: tool === id || (id === 'liquify' && tool === 'width'), onSelect: () => onTool(id) }] : [];
    }),
    // The dock's More ends with this; the tray's does too.
    { kind: 'separator' },
    ...(ADDONS_HIDDEN ? [] : [{ id: 'addons', label: props.addonName ? `Add-ons… (${props.addonName})` : 'Add-ons…', icon: 'addons' as const, checked: props.addonsOpen || tool === 'addon', onSelect: props.onAddons }]),
  ];

  const tools: ReactNode[] = [
    selectionPicker,
    <MenuSlot key="shapes" icon={shapeKind.icon} label="Shapes" on={KINDS.some((kind) => kind.id === tool)} items={shapes} />,
    shown('text') ? (textItems.length > 1 ? <MenuSlot key="text" icon="text" label="Text" on={tool === 'text'} items={textItems} /> : <Slot key="text" icon="text" label="Text" on={tool === 'text'} onPress={() => onTool('text')} />) : null,
    shown('pen') ? <Slot key="pen" icon="pen" label="Pen" on={tool === 'pen'} onPress={() => onTool('pen')} /> : null,
    shown('draw') ? <Slot key="draw" icon="draw" label="Pencil" on={tool === 'draw'} onPress={() => onTool('draw')} /> : null,
    shown('artboard') ? <Slot key="artboard" icon="artboard" label="Page" on={tool === 'artboard'} onPress={() => onTool('artboard')} /> : null,
    shown('cloner') ? <Slot key="cloner" icon="cloner" label="Cloner" on={tool === 'cloner'} onPress={() => onTool('cloner')} /> : null,
    <Slot key="insert" icon="plus" label="Insert" on={props.assetsOpen} onPress={props.onAssets} />,
    <MenuSlot key="more" icon="more" label="More" on={rest.some((one) => one.kind !== 'separator' && 'checked' in one && one.checked)} items={rest} />,
  ].filter(Boolean);

  const layerActions: ReactNode[] = [
    selectionPicker,
    <Slot key="duplicate" icon="copy" label="Duplicate" onPress={props.onDuplicate} />,
    <MenuSlot key="arrange" icon="bring-forward" label="Arrange" items={(['front', 'forward', 'backward', 'back'] as const).map((how, index) => ({ id: how, label: ['Bring to Front', 'Bring Forward', 'Send Backward', 'Send to Back'][index]!, onSelect: () => actions.arrange(how) }))} />,
    <Slot key="properties" icon="sidebar-right" label="Properties" onPress={props.onProperties} />,
    <Slot key="delete" icon="trash" label="Delete" danger onPress={props.onDelete} />,
  ];

  return (
    // Marked for the kit, which keeps its Ask button, tips and toasts above it.
    <div ref={column} data-flobi-bottom-bar className="phone-tray-column pointer-events-none absolute inset-x-0 bottom-0 z-30 flex flex-col items-center gap-[8px]">
      {props.strip ? <div className="pointer-events-auto max-w-[calc(100%-16px)]">{props.strip}</div> : null}
      <div role="toolbar" aria-label="Canvas tools" className="phone-tray create-edit-dock pointer-events-auto w-full">
        <button type="button" className="phone-tray-handle" aria-label={folded ? 'Show every tool' : 'Fold the tools to one row'} aria-expanded={!folded} onClick={() => setFolded((was) => !was)}>
          <span aria-hidden />
        </button>
        {props.editingPoints ? <div className="phone-point-context">{selectionPicker}<span>Choose points on the canvas</span></div> : editing ? (
          <div className="phone-tray-edit" key={`edit:${selectionKey}`}>
            <SelectionActionsBar {...props} />
            <div className="phone-tray-grid">{folded ? [selectionPicker] : layerActions}</div>
          </div>
        ) : (
          <div className="phone-tray-grid">{folded ? tools.slice(0, 5) : tools}</div>
        )}
      </div>
    </div>
  );
}

function Slot({ icon, label, on = false, danger = false, onPress }: { icon: IconName; label: string; on?: boolean; danger?: boolean; onPress: () => void }) {
  return (
    <button type="button" className={`phone-tray-slot ${danger ? 'phone-tray-danger' : ''}`} aria-pressed={on} onClick={onPress}>
      <span className="phone-tray-mark"><Icon name={icon} size={ICON_SIZE.navigation} /></span>
      <span className="phone-tray-label">{label}</span>
    </button>
  );
}

function MenuSlot({ icon, label, on = false, items }: { icon: IconName; label: string; on?: boolean; items: readonly MenuEntry[] }) {
  const menu = useMenu();
  return (
    <>
      <button ref={menu.ref} type="button" className="phone-tray-slot" aria-pressed={on} aria-haspopup="menu" aria-expanded={menu.open} onClick={menu.toggle}>
        <span className="phone-tray-mark"><Icon name={icon} size={ICON_SIZE.navigation} /></span>
        <span className="phone-tray-label">{label}</span>
      </button>
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} placement="above" label={label} items={items} variant="context" />
    </>
  );
}
